/**
 * The pieces that draw a member.
 *
 * The number that needs the most care on screen is how often someone voted
 * with their own party, because it is the one people quote. It is shown with
 * the count it was drawn from, and a member with nothing to divide shows a
 * dash rather than a zero — 0% and "no votes to judge on" look identical
 * once a figure is on a card, and only one of them is a claim.
 */

import { Link } from "react-router-dom";
import type { Member } from "../api";
import { PARTY_NAME, RING, TINT, partyKey, sortBy } from "./party";

/**
 * Their official portrait, or their initials.
 *
 * The images are the @unitedstates public set, which is where nearly every
 * site showing Congress gets them. Not everyone has one — delegates and
 * members who served briefly often do not — so a miss has to look
 * deliberate rather than broken, and `onError` swaps in the initials.
 */
export function Avatar({
  bioguide,
  name,
  party,
  size = 56,
}: {
  bioguide: string;
  name: string | null;
  party: string | null;
  size?: number;
}) {
  // First and last, not the first two words. Congressional names carry
  // middle initials and suffixes -- "Adelita S. Grijalva" reads as AS and
  // "Al Lawson, Jr." as AL, when the surname is the part anyone recognises.
  const words = (name ?? "?").split(/\s+/).filter((part) => /^[A-Za-z]{2,}/.test(part));
  const surname = words.filter((part) => !/^(Jr|Sr|II|III|IV)\b/i.test(part)).at(-1);
  const initials = [words[0]?.[0], words.length > 1 ? surname?.[0] : undefined]
    .filter(Boolean)
    .join("")
    .toUpperCase();

  return (
    <span
      // The party is a ring rather than a bar across the bottom of the
      // circle. Tailwind draws it as an outset shadow, so it takes the
      // colour off the photograph entirely. See `RING`.
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-sunk text-sm font-semibold text-faint ring-2 ${RING[partyKey(party)]}`}
      style={{ width: size, height: size }}
    >
      <span aria-hidden>{initials}</span>
      <img
        src={`https://unitedstates.github.io/images/congress/225x275/${bioguide}.jpg`}
        alt=""
        loading="lazy"
        width={size}
        height={size}
        className="absolute inset-0 size-full object-cover object-top"
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
      />
    </span>
  );
}

/** "Republican · Texas · House", the way a reader would say it. */
export function Affiliation({ member }: { member: Pick<Member, "party" | "state" | "chamber"> }) {
  const key = partyKey(member.party);
  return (
    <span className="text-sm text-muted">
      <span className={`font-medium ${TINT[key]}`}>{PARTY_NAME[key]}</span>
      {member.state && <> · {member.state}</>}
      {member.chamber && <> · {member.chamber === "house" ? "House" : "Senate"}</>}
    </span>
  );
}

/**
 * @param sort which ordering the list is in, so the card can show the figure
 *   it was sorted on. Showing "with party" on a list ordered by absences
 *   makes the order look arbitrary.
 */
export function MemberCard({ member, sort = "name" }: { member: Member; sort?: string }) {
  const how = sortBy(sort);
  return (
    <Link
      to={`/member/${member.bioguide}`}
      className="flex items-center gap-4 rounded-2xl border border-line bg-card p-4 transition hover:border-ink/25 hover:shadow-[0_2px_20px_-8px_rgba(0,0,0,0.18)]"
    >
      <Avatar bioguide={member.bioguide} name={member.full_name} party={member.party} />
      <span className="min-w-0 flex-1">
        {/* Wraps rather than truncates. Congressional names are long and
            "Abigail Davis Spanbe…" is not a name anyone can search for. */}
        <span className="block font-medium leading-snug">
          {member.full_name ?? member.bioguide}
        </span>
        <Affiliation member={member} />
      </span>
      <span className="shrink-0 text-right">
        <span className="tnum block text-lg font-semibold">{how.stat(member)}</span>
        <span className="block text-xs text-faint">{how.caption}</span>
      </span>
    </Link>
  );
}
